import { z } from 'zod'
import { Environment, IsoDateTime, IsoDuration, JsonSchemaDocument } from './common'

// The workflow document is what the canvas saves and exports (the owner's builder shape + conditions on edges).

export const NodePosition = z.object({ x: z.number(), y: z.number() })

export const WorkflowNode = z.object({
  id: z.string(),
  type: z.string().describe('node type id, e.g. http.connector, workflow.local, end'),
  name: z.string(),
  cfg: z.record(z.string(), z.unknown()).describe('the node config, validated by the node type’s zod schema'),
  pos: NodePosition,
})
export type WorkflowNode = z.infer<typeof WorkflowNode>

export const WorkflowEdge = z.object({
  id: z.string(),
  from: z.string().describe('source node id'),
  to: z.string().describe('target node id'),
  when: z.string().describe('an outcome of the source node, or "always"'),
})
export type WorkflowEdge = z.infer<typeof WorkflowEdge>

export const WorkflowDocument = z.object({
  nodes: z.array(WorkflowNode),
  edges: z.array(WorkflowEdge),
})
export type WorkflowDocument = z.infer<typeof WorkflowDocument>

export const TriggerKind = z.enum(['manual', 'event', 'webhook', 'page_action', 'schedule'])
export type TriggerKind = z.infer<typeof TriggerKind>

export const WorkflowKind = z.enum(['workflow', 'page'])
export type WorkflowKind = z.infer<typeof WorkflowKind>

export const Workflow = z.object({
  id: z.string(),
  workspaceId: z.string(),
  slug: z
    .string()
    .regex(/^[a-z0-9_.-]+$/)
    .describe('stable id used by other workspaces, e.g. moneyview.pl'),
  name: z.string(),
  description: z.string().default(''),
  kind: WorkflowKind.default('workflow'),
  trigger: TriggerKind,
  ownerId: z.string(),
  liveVersionId: z.string().nullable(),
  landingPageId: z.string().optional().describe('page workflows: the landing page they route'),
  tags: z.array(z.string()).default([]),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
})
export type Workflow = z.infer<typeof Workflow>

export const WorkflowInput = z.object({
  name: z.string().min(1),
  slug: z.string().regex(/^[a-z0-9_.-]+$/),
  trigger: TriggerKind,
  description: z.string().optional(),
  kind: WorkflowKind.optional(),
  landingPageId: z.string().optional(),
})
export type WorkflowInput = z.infer<typeof WorkflowInput>

export const WorkflowPatch = Workflow.pick({
  name: true,
  description: true,
  tags: true,
  ownerId: true,
}).partial()
export type WorkflowPatch = z.infer<typeof WorkflowPatch>

export const VersionStatus = z.enum(['draft', 'tested', 'live', 'retired'])
export type VersionStatus = z.infer<typeof VersionStatus>

export const CheckSeverity = z.enum(['error', 'warning'])

export const CheckResult = z.object({
  id: z.string().describe('rule id, e.g. outcomes-connected'),
  label: z.string(),
  ok: z.boolean(),
  severity: CheckSeverity,
  details: z.array(z.string()).default([]).describe('one line per problem found'),
  nodeIds: z.array(z.string()).default([]).describe('nodes the problems belong to'),
})
export type CheckResult = z.infer<typeof CheckResult>

export const WorkflowVersion = z.object({
  id: z.string(),
  workflowId: z.string(),
  number: z.number().int().min(1),
  status: VersionStatus,
  note: z.string().default(''),
  document: WorkflowDocument,
  checks: z.array(CheckResult).default([]),
  lastRunId: z.string().nullable().default(null).describe('the run that marked it tested'),
  createdAt: IsoDateTime,
  createdBy: z.string(),
  madeLiveAt: IsoDateTime.nullable().default(null),
  retiredAt: IsoDateTime.nullable().default(null),
})
export type WorkflowVersion = z.infer<typeof WorkflowVersion>

export const SaveVersionInput = z.object({
  document: WorkflowDocument,
  note: z.string().default(''),
})
export type SaveVersionInput = z.infer<typeof SaveVersionInput>

/** The export file: a workflow with one version's document. */
export const WorkflowExport = z.object({
  format: z.literal('the-kit.workflow/1'),
  workflow: z.object({
    slug: z.string(),
    name: z.string(),
    trigger: TriggerKind,
    description: z.string().default(''),
  }),
  version: z.number().int().optional(),
  document: WorkflowDocument,
})
export type WorkflowExport = z.infer<typeof WorkflowExport>

/** A live workflow seen as a node by other workspaces (entry 31, R2). */
export const NodeCard = z.object({
  workspaceId: z.string(),
  workflowId: z.string(),
  workflowSlug: z.string(),
  version: z.number().int(),
  name: z.string(),
  description: z.string().default(''),
  status: z.literal('live'),
  modes: z.array(z.enum(['sync', 'async'])),
  typicalTime: z.object({ sync: IsoDuration.optional(), async: IsoDuration.optional() }),
  input: JsonSchemaDocument,
  outputs: z.array(z.string()).describe('the End nodes’ outcomes + error + timed_out'),
  output: JsonSchemaDocument,
})
export type NodeCard = z.infer<typeof NodeCard>

export const SimulationRequest = z.object({
  versionId: z.string(),
  environment: Environment,
  input: z.record(z.string(), z.unknown()),
  waitSeconds: z.number().int().min(0).max(60).default(0),
})
export type SimulationRequest = z.infer<typeof SimulationRequest>
